import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { NETWORK_BADGE, NETWORK_LABELS } from "./constants";

/**
 * Three filter primitives stacked above the brands list:
 *
 *   • CountryChips — appears only when at least one assigned brand
 *                    has geo data. Top-10 most-covered ISO-2 codes
 *                    by count, with counts including +global brands.
 *   • NetworkChips — appears only when admin has flipped
 *                    `disclose_networks=true` for this pub. Uses the
 *                    same WCAG-friendly per-network palette as the
 *                    badge inline on each row.
 *   • SearchBox    — case-insensitive substring match on brand and
 *                    advertiser names. Clear-button visible while
 *                    the input is non-empty.
 *
 * Pure presentation — all state lives in PubBrands.js so the filter
 * reset link inside the empty-state can clear country + network +
 * query in one call.
 */

export function CountryChips({ country, onChange, portfolioTotal, countryStats }) {
  if (!countryStats?.top?.length) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="country-filter-row">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mr-1">
        Country
      </span>
      <button
        type="button"
        onClick={() => onChange("all")}
        data-testid="country-chip-all"
        className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
          country === "all"
            ? "bg-[#002FA7] text-white border-[#002FA7]"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
        }`}
      >
        All <span className="opacity-60 ml-1">{portfolioTotal}</span>
      </button>
      {countryStats.top.map(([cc, n]) => (
        <button
          key={cc}
          type="button"
          onClick={() => onChange(cc)}
          data-testid={`country-chip-${cc}`}
          title={`${n} brand${n === 1 ? "" : "s"} available in ${cc}${countryStats.global ? ` (+ ${countryStats.global} global)` : ""}`}
          className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
            country === cc
              ? "bg-[#002FA7] text-white border-[#002FA7]"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}
        >
          {cc} <span className="opacity-60 ml-1">{n + (countryStats.global || 0)}</span>
        </button>
      ))}
      {countryStats.global > 0 && country === "all" && (
        <span className="text-[10px] text-slate-400 ml-1" title="Brands with no geo restriction">
          + {countryStats.global} global
        </span>
      )}
    </div>
  );
}

export function NetworkChips({ network, onChange, portfolioTotal, networkStats, discloseNetworks }) {
  if (!discloseNetworks || !networkStats?.length) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="network-filter-row">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mr-1">
        Feed Network
      </span>
      <button
        type="button"
        onClick={() => onChange("all")}
        data-testid="network-chip-all"
        className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
          network === "all"
            ? "bg-[#002FA7] text-white border-[#002FA7]"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
        }`}
      >
        All <span className="opacity-60 ml-1">{portfolioTotal}</span>
      </button>
      {networkStats.map(([nk, n]) => {
        const active = network === nk;
        const tone = NETWORK_BADGE[nk] || "bg-slate-100 text-slate-700 border-slate-200";
        return (
          <button
            key={nk}
            type="button"
            onClick={() => onChange(nk)}
            data-testid={`network-chip-${nk}`}
            className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
              active
                ? "bg-[#002FA7] text-white border-[#002FA7]"
                : `${tone} hover:brightness-95`
            }`}
          >
            {NETWORK_LABELS[nk] || nk} <span className="opacity-60 ml-1">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SearchBox({ query, onChange }) {
  return (
    <div className="mb-4 relative max-w-md" data-testid="pub-brands-search-row">
      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <Input
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search by brand or advertiser…"
        className="pl-9 h-9 text-sm"
        data-testid="pub-brands-search"
      />
      {query && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
          data-testid="pub-brands-search-clear"
        >
          Clear
        </button>
      )}
    </div>
  );
}
